import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DocumentTextIcon,
  PlusCircleIcon,
  ListBulletIcon,
  UserIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  SparklesIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Role } from '../../types';
import { useMasterData } from '../../hooks/useMasterData';
import { useDateContext } from '../../hooks/useDate';
import { useAuth } from '../../hooks/useAuth';
import { useAuthorization } from '../../hooks/useAuthorization';
import { useCaseHelpers } from '../../hooks/useCaseHelpers';
import { getCaseStatusText } from '../../utils/statusUtils';
import logger from '../../utils/logger';
import { Button, Badge, PageTitle, PaginatedTable, PaginatedCards } from '../../components/ui';
import { VehicleInspectionStatus } from '../../components/ui/VehicleInspectionStatus';
import { CasePrintToolbar } from '../../components/cases/CasePrintToolbar';
import { generateCasePDF } from '../../utils/pdfGenerator';
import { convertCasesToCsvData, validateCsvDataQuality } from '../../utils/caseListCsvExport';
import { exportToCSV } from '../../utils/csvExport';
import type { Case } from '../../types';

// ステータス表示関数は utils/statusUtils.ts に統一

type ViewMode = 'panel' | 'table';

const CaseListPage: React.FC = () => {
  const {
    cases,
    refreshData,
    refreshLandTransporters,
    loading,
    getBusinessPartnerById,
    getVehicleModelById,
    getManufacturerById,
    landTransporters,
  } = useMasterData();

  // 表示モードの状態管理（LocalStorageから復元）
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('caseViewMode');
    return (saved as ViewMode) || 'panel';
  });

  // 印刷・PDF出力の状態管理
  const [isExporting, setIsExporting] = useState(false);
  const {
    safe: {
      getVehicleName,
      getBillingPartnerName,
      getPickupPartnerName,
      getDeliveryPartnerName,
      getTransporterName,
    },
    formatDateForDisplay,
  } = useCaseHelpers();
  const { selectedDate, setSelectedDate, goToPreviousDay, goToNextDay } = useDateContext();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { checkPermission } = useAuthorization();

  // 権限チェック
  const canCreateCase = checkPermission([Role.ADMIN, Role.DISPATCHER]);
  const isDriver = user?.role === Role.DRIVER;

  // ページマウント時にデータを更新（陸送員データを確実に取得）
  useEffect(() => {
    const loadDataWithRetry = async () => {
      logger.info('🔄 CaseListPage: データ読み込み開始');

      // まず陸送員データを確実に取得
      await refreshLandTransporters();

      // その後で全体データを取得
      await refreshData();

      // データ取得後の確認ログ
      logger.info('✅ CaseListPage: データ読み込み完了');
    };

    loadDataWithRetry();
  }, []); // 依存配列を空にして無限ループを防ぐ

  // ページがフォーカスされた時にもデータを更新（他のページで変更があった場合）
  useEffect(() => {
    const handleFocus = () => {
      refreshData();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refreshData]);

  const filteredCases = useMemo(() => {
    let casesToShow = cases;

    // 陸送員の場合は自分に割り当てられた案件のみ表示
    if (isDriver && user?.landTransporterId) {
      casesToShow = cases.filter(c => c.assignedLandTransporterId === user.landTransporterId);
    }

    // 日付フィルタリング
    const filtered = casesToShow.filter(c => {
      if (!c.pickupDate) return false;
      return c.pickupDate === selectedDate;
    });

    return filtered.sort((a, b) => a.pickupTimeStart.localeCompare(b.pickupTimeStart));
  }, [cases, selectedDate, isDriver, user?.landTransporterId]);

  const unassignedCases = useMemo(() => {
    return filteredCases.filter(c => !c.assignedLandTransporterId);
  }, [filteredCases]);

  const handleCardClick = (caseId: string) => {
    navigate(`/cases/${caseId}`);
  };

  // AI配車機能 - スケジュールページにリダイレクト
  const handleAiDispatch = () => {
    if (unassignedCases.length === 0) {
      alert('未割り当ての案件がありません。');
      return;
    }

    // スケジュールページのAI配車モードにリダイレクト
    navigate('/schedule?ai=true');
  };

  // 表示モード変更
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('caseViewMode', mode);
  };

  // 印刷処理
  const handlePrint = () => {
    const printArea = document.getElementById('print-area');
    if (!printArea) {
      logger.error('印刷エリアが見つかりません');
      return;
    }

    // 印刷対象をprint-areaのみに限定
    const originalBodyContent = document.body.innerHTML;

    // print-areaをクローンして印刷用ヘッダー・フッターを追加
    const clonedPrintArea = printArea.cloneNode(true) as HTMLElement;

    // 印刷用ヘッダーを作成
    const header = document.createElement('div');
    header.className = 'print-header print-only';
    header.innerHTML = `
      <h1>案件一覧</h1>
      <div class="print-date">出力日時: ${new Date().toLocaleString('ja-JP')}</div>
    `;

    // 印刷用フッターを作成
    const footer = document.createElement('div');
    footer.className = 'print-footer print-only';
    footer.innerHTML = `
      <div>株式会社サンプル陸送 - 案件一覧出力</div>
    `;

    // ヘッダーとフッターをクローンに追加
    clonedPrintArea.insertBefore(header, clonedPrintArea.firstChild);
    clonedPrintArea.appendChild(footer);

    // 印刷用の一時的な構造を作成
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = `
      <div class="print-container">
        ${clonedPrintArea.outerHTML}
      </div>
    `;

    // bodyの内容を一時的に印刷用に置き換え
    document.body.innerHTML = tempDiv.innerHTML;

    // 印刷実行
    window.print();

    // 元の内容に復元
    document.body.innerHTML = originalBodyContent;

    // イベントリスナーの再初期化が必要な場合のため、少し待機
    setTimeout(() => {
      // 必要に応じてReactの再レンダリングを促す処理
      window.location.reload();
    }, 100);
  };

  // PDF出力処理
  const handlePdfExport = async () => {
    const printArea = document.getElementById('print-area');
    if (!printArea) {
      logger.error('印刷エリアが見つかりません');
      return;
    }

    setIsExporting(true);

    try {
      const result = await generateCasePDF(printArea, viewMode, {
        filename: `cases_${selectedDate.replace(/-/g, '')}.pdf`,
      });

      if (result.success) {
        logger.info('PDF出力完了', { filename: result.filename });
      } else {
        logger.error('PDF出力エラー', { error: result.error });
        alert(`PDF出力エラー: ${result.error}`);
      }
    } catch (error) {
      logger.error('PDF出力処理エラー', error);
      alert('PDF出力中にエラーが発生しました');
    } finally {
      setIsExporting(false);
    }
  };

  // CSV出力処理
  const handleCsvExport = () => {
    try {
      logger.info('CSV出力開始', { caseCount: filteredCases.length });

      // キャッシュマップを作成（大量データ対応）
      const partnerCache = new Map<string, string>();
      const vehicleCache = new Map<string, string>();
      const transporterCache = new Map<string, string>();

      // ヘルパー関数オブジェクトを作成（コンポーネントトップレベルで取得済みの関数を使用）
      const helpers = {
        getBillingPartnerName: (id: string | null | undefined) => {
          if (!id) return '不明';
          if (!partnerCache.has(id)) {
            const partner = getBusinessPartnerById(id);
            partnerCache.set(id, partner ? partner.displayName || partner.name : '不明');
          }
          return partnerCache.get(id)!;
        },
        getPickupPartnerName: (id: string | null | undefined) => {
          if (!id) return '不明';
          if (!partnerCache.has(id)) {
            const partner = getBusinessPartnerById(id);
            partnerCache.set(id, partner ? partner.displayName || partner.name : '不明');
          }
          return partnerCache.get(id)!;
        },
        getDeliveryPartnerName: (id: string | null | undefined) => {
          if (!id) return '不明';
          if (!partnerCache.has(id)) {
            const partner = getBusinessPartnerById(id);
            partnerCache.set(id, partner ? partner.displayName || partner.name : '不明');
          }
          return partnerCache.get(id)!;
        },
        getVehicleName: (id: string | null | undefined) => {
          if (!id) return '車両名不明';
          if (!vehicleCache.has(id)) {
            const model = getVehicleModelById(id);
            if (!model) {
              vehicleCache.set(id, '車両名不明');
            } else {
              const manufacturer = getManufacturerById(model.manufacturerId);
              vehicleCache.set(
                id,
                manufacturer ? `${manufacturer.name} ${model.name}` : model.name
              );
            }
          }
          return vehicleCache.get(id)!;
        },
        getTransporterName: (id: string | null | undefined) => {
          if (!id) return '未割当';
          if (!transporterCache.has(id)) {
            const transporter = landTransporters.find(t => t.id === id);
            transporterCache.set(id, transporter ? transporter.name : '不明な陸送員');
          }
          return transporterCache.get(id)!;
        },
      };

      // データ品質検証
      const quality = validateCsvDataQuality(filteredCases, helpers);

      // 不完全なデータがある場合はログに記録
      if (quality.incompleteCount > 0) {
        logger.warn('CSV出力に不完全データが含まれている', {
          incompleteCount: quality.incompleteCount,
          issues: quality.issues,
        });
      }

      // 案件データをCSV形式に変換
      const csvData = convertCasesToCsvData(filteredCases, helpers);

      // CSV出力実行
      exportToCSV(csvData);

      logger.info('CSV出力完了', { filename: csvData.filename });
    } catch (error) {
      logger.error('CSV出力エラー', error);
    }
  };

  return (
    <div>
      <PageTitle icon={DocumentTextIcon} title='案件管理' />

      <div className='mb-4 sm:mb-6'>
        <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0'>
          <h2 className='text-lg sm:text-xl font-semibold text-gray-700 dark:text-white flex flex-col sm:flex-row sm:items-center'>
            <div className='flex items-center'>
              <ListBulletIcon className='h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3' />
              <span>案件一覧 ({filteredCases.length})</span>
            </div>
            {unassignedCases.length > 0 && (
              <span className='ml-0 sm:ml-2 mt-1 sm:mt-0 px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs sm:text-sm rounded-full self-start'>
                未割当 {unassignedCases.length}件
              </span>
            )}
          </h2>
          <div className='flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4'>
            {/* 印刷・PDF・CSV出力ツールバー */}
            <CasePrintToolbar
              viewMode={viewMode}
              onViewModeChange={handleViewModeChange}
              onPrint={handlePrint}
              onPdfExport={handlePdfExport}
              onCsvExport={handleCsvExport}
              isExporting={isExporting}
            />
            {canCreateCase && (
              <Button
                onClick={() => navigate('/cases/new')}
                variant='primary'
                size='md'
                className='w-full sm:w-auto'
              >
                <PlusCircleIcon className='h-5 w-5 mr-2' />
                <span className='hidden sm:inline'>新規案件を追加</span>
                <span className='sm:hidden'>新規</span>
              </Button>
            )}
          </div>
        </div>

        {/* 日付ナビゲーション */}
        <div className='mt-4 flex items-center justify-center space-x-2 sm:space-x-4'>
          <Button
            onClick={goToPreviousDay}
            variant='secondary'
            size='md'
            className='min-h-[44px] min-w-[44px]'
            aria-label='前日'
          >
            <ChevronLeftIcon className='h-5 w-5' />
          </Button>

          <div className='flex items-center space-x-3 mx-2 sm:mx-4'>
            <div className='text-center'>
              <div className='text-lg sm:text-2xl font-bold text-gray-800 dark:text-white'>
                {formatDateForDisplay(selectedDate)}
              </div>
            </div>
          </div>

          <div className='relative'>
            <input
              type='date'
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              className='absolute opacity-0 w-full h-full cursor-pointer min-h-[44px] min-w-[44px]'
              aria-label='日付選択'
            />
            <Button variant='secondary' size='md' className='min-h-[44px] min-w-[44px]'>
              <CalendarIcon className='h-5 w-5' />
            </Button>
          </div>

          <Button
            onClick={goToNextDay}
            variant='secondary'
            size='md'
            className='min-h-[44px] min-w-[44px]'
            aria-label='翌日'
          >
            <ChevronRightIcon className='h-5 w-5' />
          </Button>
        </div>
      </div>

      {/* AI配車ボタン（未割り当て案件がある場合、陸送員以外） */}
      {!isDriver && unassignedCases.length > 0 && (
        <div className='mb-4 sm:mb-6'>
          <Button
            onClick={handleAiDispatch}
            variant='primary'
            size='lg'
            className='w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 border-purple-600 shadow-lg hover:shadow-xl transition-all duration-200'
          >
            <SparklesIcon className='h-5 w-5 mr-2' />
            <span className='text-sm sm:text-base md:text-lg whitespace-nowrap'>
              AI配車 ({unassignedCases.length}件)
            </span>
          </Button>
        </div>
      )}

      {/* 案件表示エリア */}
      <div id='print-area'>
        {viewMode === 'panel' ? (
          <PaginatedCards
            data={filteredCases}
            loading={loading}
            storageKey='caseList'
            keyExtractor='id'
            emptyMessage='案件がありません'
            renderCard={case_ => (
              <div
                onClick={() => handleCardClick(case_.id)}
                className={`rounded-lg shadow-md p-4 sm:p-6 cursor-pointer card-hover touch-target fade-in ${
                  !case_.assignedLandTransporterId
                    ? 'bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800'
                    : 'bg-white dark:bg-gray-800'
                }`}
              >
                <div className='flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4 space-y-2 sm:space-y-0'>
                  <div className='flex-1 min-w-0'>
                    {case_.caseNumber && (
                      <p className='text-xs text-blue-600 dark:text-blue-400 font-medium mb-1'>
                        案件番号: {case_.caseNumber}
                      </p>
                    )}
                    <h3 className='text-base sm:text-lg font-bold text-gray-800 dark:text-white truncate'>
                      {getBillingPartnerName(case_)}
                    </h3>
                    <p className='text-xs sm:text-sm text-gray-500 dark:text-gray-400'>
                      {getVehicleName(case_)}
                    </p>
                    {case_.vehiclePlateNumber && (
                      <p className='text-xs text-gray-600 dark:text-gray-400 font-mono mt-1'>
                        {case_.vehiclePlateNumber}
                      </p>
                    )}
                  </div>
                  <div className='flex items-center gap-1'>
                    {/* AcceptanceStatusBadge removed - no longer needed */}
                    <Badge
                      variant={
                        case_.status === 'completed'
                          ? 'success'
                          : case_.status === 'in_transit'
                            ? 'info'
                            : case_.status === 'assigned'
                              ? 'warning'
                              : 'secondary'
                      }
                      size='sm'
                    >
                      {getCaseStatusText(case_.status)}
                    </Badge>
                  </div>
                </div>

                <div className='space-y-2 sm:space-y-3'>
                  <div className='flex items-start text-xs sm:text-sm text-gray-600 dark:text-gray-300'>
                    <span className='font-medium w-12 sm:w-16 flex-shrink-0'>引取:</span>
                    <span className='truncate'>{getPickupPartnerName(case_)}</span>
                  </div>
                  <div className='flex items-start text-xs sm:text-sm text-gray-600 dark:text-gray-300'>
                    <span className='font-medium w-12 sm:w-16 flex-shrink-0'>搬入:</span>
                    <span className='truncate'>{getDeliveryPartnerName(case_)}</span>
                  </div>
                  <div className='flex items-center text-xs sm:text-sm text-gray-600 dark:text-gray-300'>
                    <ClockIcon className='h-4 w-4 text-blue-500 mr-2 flex-shrink-0' />
                    <span className='font-medium mr-2'>引取:</span>
                    <span className='text-xs sm:text-sm font-mono'>
                      {case_.pickupTimeStart}
                      {case_.pickupTimeEnd &&
                        case_.pickupTimeEnd !== case_.pickupTimeStart &&
                        ` - ${case_.pickupTimeEnd}`}
                    </span>
                  </div>
                  <div className='flex items-center text-xs sm:text-sm text-gray-600 dark:text-gray-300'>
                    <ClockIcon className='h-4 w-4 text-green-500 mr-2 flex-shrink-0' />
                    <span className='font-medium mr-2'>搬入:</span>
                    <span className='text-xs sm:text-sm font-mono'>
                      {case_.deliveryTime || '未定'}
                    </span>
                  </div>

                  {/* FL-113: 車検状態表示 */}
                  {(case_.isVehicleInspectionExpired || case_.requiresTemporaryNumber) && (
                    <div className='flex items-center'>
                      <VehicleInspectionStatus
                        isExpired={case_.isVehicleInspectionExpired}
                        requiresTemporaryNumber={case_.requiresTemporaryNumber}
                        temporaryNumberNotes={case_.temporaryNumberNotes}
                        size='small'
                        variant='badge'
                      />
                    </div>
                  )}

                  <div className='flex items-start text-xs sm:text-sm text-gray-600 dark:text-gray-300'>
                    {!case_.assignedLandTransporterId ? (
                      <>
                        <ExclamationTriangleIcon className='h-4 w-4 mr-2 text-red-500 flex-shrink-0 mt-0.5' />
                        <span className='text-red-600 dark:text-red-400 font-medium text-xs sm:text-sm'>
                          陸送員の割り当てが必要です
                        </span>
                      </>
                    ) : case_.assignedLandTransporterId ? (
                      <>
                        <UserIcon className='h-4 w-4 mr-2 flex-shrink-0 mt-0.5' />
                        <span className='truncate'>
                          {getTransporterName(case_.assignedLandTransporterId)}
                        </span>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            )}
          />
        ) : (
          <PaginatedTable
            data={filteredCases}
            loading={loading}
            storageKey='caseList'
            onRowClick={case_ => handleCardClick(case_.id)}
            columns={[
              {
                key: 'billingPartner',
                label: '請求先',
                render: (record: Case) => (
                  <div>
                    <div className='font-medium text-gray-900 dark:text-white text-sm truncate'>
                      {getBillingPartnerName(record)}
                    </div>
                    <div className='text-xs text-gray-500 dark:text-gray-400 truncate'>
                      {getVehicleName(record)}
                    </div>
                  </div>
                ),
              },
              {
                key: 'pickup',
                label: '引取先',
                render: (record: Case) => (
                  <span className='text-sm text-gray-700 dark:text-gray-300 truncate block'>
                    {getPickupPartnerName(record)}
                  </span>
                ),
              },
              {
                key: 'delivery',
                label: '搬入先',
                render: (record: Case) => (
                  <span className='text-sm text-gray-700 dark:text-gray-300 truncate block'>
                    {getDeliveryPartnerName(record)}
                  </span>
                ),
              },
              {
                key: 'time',
                label: '時間',
                render: (record: Case) => (
                  <div className='text-xs space-y-2'>
                    <div className='flex items-center text-gray-700 dark:text-gray-300'>
                      <ClockIcon className='h-3 w-3 text-blue-500 mr-1.5 flex-shrink-0' />
                      <span className='font-medium mr-1'>引取:</span>
                      <span className='font-mono'>
                        {record.pickupTimeStart}
                        {record.pickupTimeEnd &&
                          record.pickupTimeEnd !== record.pickupTimeStart &&
                          `-${record.pickupTimeEnd}`}
                      </span>
                    </div>
                    <div className='flex items-center text-gray-600 dark:text-gray-400'>
                      <ClockIcon className='h-3 w-3 text-green-500 mr-1.5 flex-shrink-0' />
                      <span className='font-medium mr-1'>搬入:</span>
                      <span className='font-mono'>{record.deliveryTime || '未定'}</span>
                    </div>
                  </div>
                ),
              },
              {
                key: 'driver',
                label: '陸送員',
                render: (record: Case) => {
                  if (!record.assignedLandTransporterId) {
                    return (
                      <span className='text-xs text-red-600 dark:text-red-400 font-medium'>
                        未割当
                      </span>
                    );
                  }
                  return (
                    <span className='text-xs text-gray-700 dark:text-gray-300'>
                      {getTransporterName(record.assignedLandTransporterId)}
                    </span>
                  );
                },
              },
              {
                key: 'status',
                label: 'ステータス',
                render: (record: Case) => (
                  <div className='flex items-center gap-1'>
                    {/* AcceptanceStatusBadge removed - no longer needed */}
                    <Badge
                      variant={
                        record.status === 'completed'
                          ? 'success'
                          : record.status === 'in_transit'
                            ? 'info'
                            : record.status === 'assigned'
                              ? 'warning'
                              : 'secondary'
                      }
                      size='sm'
                    >
                      {getCaseStatusText(record.status)}
                    </Badge>
                  </div>
                ),
              },
            ]}
            rowKey='id'
            emptyMessage='案件がありません'
          />
        )}
      </div>
    </div>
  );
};

export default CaseListPage;
